import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Select from '@cloudscape-design/components/select';
import FormField from '@cloudscape-design/components/form-field';
import Alert from '@cloudscape-design/components/alert';
import Spinner from '@cloudscape-design/components/spinner';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import { Prompt, PromptVersion, getPromptHistory } from './api';

interface Props {
  prompt: Prompt;
  idToken: string;
  onDismiss: () => void;
}

type DiffLine = { type: 'same' | 'add' | 'del'; text: string };

// Line-level diff via longest-common-subsequence. Small prompts, so an O(n*m)
// table is fine and keeps the dependency footprint at zero.
function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split('\n');
  const b = newText.split('\n');
  const n = a.length;
  const m = b.length;
  const at = (arr: string[], k: number): string => arr[k] ?? '';
  // lcs[i][j] = length of the longest common subsequence of a[i:] and b[j:].
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  const cell = (i: number, j: number): number => lcs[i]?.[j] ?? 0;
  for (let i = n - 1; i >= 0; i--) {
    const row = lcs[i];
    if (!row) continue;
    for (let j = m - 1; j >= 0; j--) {
      row[j] = at(a, i) === at(b, j) ? cell(i + 1, j + 1) + 1 : Math.max(cell(i + 1, j), cell(i, j + 1));
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (at(a, i) === at(b, j)) {
      out.push({ type: 'same', text: at(a, i) });
      i++;
      j++;
    } else if (cell(i + 1, j) >= cell(i, j + 1)) {
      out.push({ type: 'del', text: at(a, i) });
      i++;
    } else {
      out.push({ type: 'add', text: at(b, j) });
      j++;
    }
  }
  while (i < n) out.push({ type: 'del', text: at(a, i++) });
  while (j < m) out.push({ type: 'add', text: at(b, j++) });
  return out;
}

function versionLabel(v: PromptVersion): string {
  const who = v.updatedBy || 'unknown';
  const when = v.updatedAt || v.versionId;
  const num = v.versionNumber != null ? `v${v.versionNumber} — ` : '';
  return `${num}${when} — ${who}${v.isDefault ? ' (reset to default)' : ''}`;
}

export default function PromptHistoryModal({ prompt, idToken, onDismiss }: Props) {
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [oldId, setOldId] = useState<string | null>(null);
  const [newId, setNewId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hist = await getPromptHistory(idToken, prompt.key);
      setVersions(hist);
      // Default: compare the two most recent versions (newest first).
      const newest = hist[0];
      const previous = hist[1];
      if (newest) {
        setNewId(newest.versionId);
        setOldId((previous ?? newest).versionId);
      }
    } catch {
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [idToken, prompt.key]);

  useEffect(() => {
    void load();
  }, [load]);

  const byId = useMemo(() => {
    const map = new Map<string, PromptVersion>();
    versions.forEach((v) => map.set(v.versionId, v));
    return map;
  }, [versions]);

  const options = versions.map((v) => ({ value: v.versionId, label: versionLabel(v) }));

  const oldVersion = oldId ? byId.get(oldId) : undefined;
  const newVersion = newId ? byId.get(newId) : undefined;

  const diff = useMemo(() => {
    if (!oldVersion || !newVersion) return [];
    return diffLines(oldVersion.content, newVersion.content);
  }, [oldVersion, newVersion]);

  const colorFor = (t: DiffLine['type']) =>
    t === 'add' ? '#e6ffed' : t === 'del' ? '#ffeef0' : 'transparent';
  const signFor = (t: DiffLine['type']) => (t === 'add' ? '+' : t === 'del' ? '-' : ' ');

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      size="max"
      header={`History — ${prompt.label}`}
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            Close
          </Button>
        </Box>
      }
    >
      {loading ? (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
        </Box>
      ) : error ? (
        <Alert type="error" action={<Button onClick={() => void load()}>Retry</Button>}>
          {error}
        </Alert>
      ) : versions.length === 0 ? (
        <Alert type="info">No history yet. Versions are recorded from the next edit onward.</Alert>
      ) : (
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Compare from (older)">
              <Select
                selectedOption={options.find((o) => o.value === oldId) ?? null}
                onChange={({ detail }) => setOldId(detail.selectedOption.value ?? null)}
                options={options}
              />
            </FormField>
            <FormField label="To (newer)">
              <Select
                selectedOption={options.find((o) => o.value === newId) ?? null}
                onChange={({ detail }) => setNewId(detail.selectedOption.value ?? null)}
                options={options}
              />
            </FormField>
          </ColumnLayout>

          <Box>
            <SpaceBetween direction="horizontal" size="xs">
              <Badge color="green">+ added</Badge>
              <Badge color="red">- removed</Badge>
              <span>{versions.length} version(s) recorded</span>
            </SpaceBetween>
          </Box>

          {oldId === newId ? (
            <Alert type="info">Select two different versions to see a diff.</Alert>
          ) : (
            <Box>
              <pre
                style={{
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: '1px solid #eaeded',
                  borderRadius: 4,
                  overflow: 'auto',
                  maxHeight: '55vh',
                }}
              >
                {diff.map((line, idx) => (
                  <div key={idx} style={{ backgroundColor: colorFor(line.type) }}>
                    <span style={{ userSelect: 'none', opacity: 0.6 }}>{signFor(line.type)} </span>
                    {line.text || ' '}
                  </div>
                ))}
              </pre>
            </Box>
          )}
        </SpaceBetween>
      )}
    </Modal>
  );
}
